import React from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { StrapiWorkOrderRow } from '../../../../../api/strapiCrm';
import { WorkOrderStatusTooltip } from '../../../WorkOrderStatusHover';
import { workOrderStatusDisplay } from '../../../workOrderShared';
import { romanMonthMenuItems } from '../../../workOrderRomanMonth';

function shortId(documentId: string): string {
  if (documentId.length <= 12) return documentId;
  return `${documentId.slice(0, 8)}…`;
}

const readOnlySlot = { input: { readOnly: true } } as const;

function romanQuarterLabel(raw: unknown): string {
  const v = raw != null && String(raw).trim() !== '' ? String(raw).trim() : '';
  if (!v) return '—';
  const hit = romanMonthMenuItems().find((o) => o.value === v);
  return hit ? hit.label : v;
}

/** Konteks atas: ID WO, jumlah sampel, quotation — sama pola dengan ringkasan di form buat work order */
export function WorkOrderDetailIntroBlurb({ wo }: { wo: StrapiWorkOrderRow }) {
  const q = wo.quotation;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        ID work order: <strong>{shortId(wo.documentId)}</strong>
        {' · '}
        Jumlah sampel: <strong>{wo.sampleCount}</strong>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Quotation:{' '}
        {q ? (
          <>
            <strong>{q.quotation_number}</strong>
          </>
        ) : (
          '—'
        )}
      </Typography>
    </Box>
  );
}

/** Detail & jadwal (read-only) — selaras kolom pada tab pertama form buat work order */
export function WorkOrderDetailScheduleReadonlyFields({ wo }: { wo: StrapiWorkOrderRow }) {
  return (
    <Stack spacing={2} sx={{ maxWidth: 560 }}>
      <TextField
        label="Job number (work order)"
        value={wo.job_number?.trim() ? wo.job_number : '—'}
        fullWidth
        size="small"
        slotProps={readOnlySlot}
        sx={{ '& .MuiInputBase-input': { cursor: 'default' } }}
        helperText="Nomor GL diisi otomatis saat work order dibuat."
      />
      <TextField
        label="Tanggal work order"
        value={wo.work_order_date?.slice(0, 10) ?? ''}
        fullWidth
        size="small"
        InputLabelProps={{ shrink: true }}
        slotProps={readOnlySlot}
        sx={{ '& .MuiInputBase-input': { cursor: 'default' } }}
      />
      <WorkOrderStatusTooltip status={wo.progress_status}>
        <TextField
          label="Status"
          value={workOrderStatusDisplay(wo.progress_status)}
          fullWidth
          size="small"
          slotProps={{
            input: { readOnly: true },
            htmlInput: { 'data-testid': 'work-order-detail-status' },
          }}
          sx={{ '& .MuiInputBase-input': { cursor: 'default' } }}
        />
      </WorkOrderStatusTooltip>
      <TextField
        label="Bulan (angka Romawi)"
        value={romanQuarterLabel(wo.roman_quarter)}
        fullWidth
        size="small"
        helperText="I = Januari … XII = Desember."
        slotProps={readOnlySlot}
        sx={{ '& .MuiInputBase-input': { cursor: 'default' } }}
      />
      <TextField
        label="Tahun kalender"
        value={wo.calendar_year != null ? String(wo.calendar_year) : '—'}
        fullWidth
        size="small"
        slotProps={readOnlySlot}
        sx={{ '& .MuiInputBase-input': { cursor: 'default' } }}
      />
      <TextField
        label="Mulai pengujian"
        value={wo.testing_start_date ?? '—'}
        fullWidth
        size="small"
        slotProps={readOnlySlot}
        sx={{ '& .MuiInputBase-input': { cursor: 'default' } }}
      />
      <TextField
        label="Selesai pengujian"
        value={wo.testing_end_date ?? '—'}
        fullWidth
        size="small"
        slotProps={readOnlySlot}
        sx={{ '& .MuiInputBase-input': { cursor: 'default' } }}
      />
    </Stack>
  );
}

/** Aksi bawah seperti footer form buat / ubah WO */
export function WorkOrderDetailViewFooterActions({
  documentId,
  canEdit = false,
  onPreviewPdf,
}: {
  documentId: string;
  /** Hanya Admin Work Order yang boleh ubah — hide tombol jika false. */
  canEdit?: boolean;
  onPreviewPdf?: () => void;
}) {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {onPreviewPdf ? (
        <Button
          type="button"
          variant="outlined"
          size="small"
          onClick={onPreviewPdf}
          data-testid="work-order-detail-button-pdf"
        >
          Generate PDF
        </Button>
      ) : null}
      {canEdit ? (
        <Button
          component={RouterLink}
          to={`/sampling/work-order/${encodeURIComponent(documentId)}/edit`}
          variant="outlined"
          size="small"
          data-testid="work-order-detail-button-edit"
        >
          Ubah work order
        </Button>
      ) : null}
      <Button component={RouterLink} to="/sampling/work-orders" size="small">
        Ke daftar work order
      </Button>
    </Stack>
  );
}
